import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportCaseDetail } from '../types/supportCase';
import type { Booking } from '../types/booking';
import type { SupportActionType } from '../types/supportCase';
import { VERSION_REQUIRED_ACTIONS } from '../types/supportCase';

interface Props {
  readonly supportCase: SupportCaseDetail;
  readonly booking: Booking | undefined;
  readonly isSubmitting: boolean;
  readonly onSubmit: (params: {
    actionType: SupportActionType;
    idempotencyKey: string;
    expectedBookingVersion?: number;
    note?: string;
  }) => void;
  readonly onCancel: () => void;
}

/** Actions available for each secureBookingTokenState value. */
function eligibleActions(booking: Booking | undefined): ReadonlySet<SupportActionType> {
  if (!booking) return new Set();
  const eligible = new Set<SupportActionType>();

  const tokenState = booking.secureBookingTokenState;
  if (tokenState === 'ACTIVE') eligible.add('RESEND_PAYMENT_ACCESS');
  if (tokenState === 'EXPIRED') eligible.add('RESET_EXPIRED_ACCESS');

  const status = booking.status;
  if (status === 'AWAITING_GUEST_PAYMENT') {
    eligible.add('RECORD_OFFLINE_DEPOSIT');
    eligible.add('RECORD_OFFLINE_FULL_PAYMENT');
  }
  if (status === 'DEPOSIT_PAID_AWAITING_BALANCE') {
    eligible.add('RECORD_OFFLINE_FULL_PAYMENT');
  }

  return eligible;
}

const ALL_ACTION_TYPES: ReadonlyArray<SupportActionType> = [
  'RESEND_PAYMENT_ACCESS',
  'RESET_EXPIRED_ACCESS',
  'RECORD_OFFLINE_DEPOSIT',
  'RECORD_OFFLINE_FULL_PAYMENT',
];

const ACTION_TYPE_I18N: Record<
  SupportActionType,
  | 'badges:actionTypeResendPaymentAccess'
  | 'badges:actionTypeResetExpiredAccess'
  | 'badges:actionTypeRecordOfflineDeposit'
  | 'badges:actionTypeRecordOfflineFullPayment'
> = {
  RESEND_PAYMENT_ACCESS: 'badges:actionTypeResendPaymentAccess',
  RESET_EXPIRED_ACCESS: 'badges:actionTypeResetExpiredAccess',
  RECORD_OFFLINE_DEPOSIT: 'badges:actionTypeRecordOfflineDeposit',
  RECORD_OFFLINE_FULL_PAYMENT: 'badges:actionTypeRecordOfflineFullPayment',
};

export const SupportCaseActionForm = ({
  supportCase,
  booking,
  isSubmitting,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['badges', 'forms', 'pages', 'common']);
  const idPrefix = useId();

  const isTerminal =
    supportCase.status === 'RESOLVED' || supportCase.status === 'ABANDONED';

  const eligible = eligibleActions(booking);
  const availableActions = ALL_ACTION_TYPES.filter(a => eligible.has(a));

  const [actionType, setActionType] = useState<SupportActionType | ''>(
    availableActions[0] ?? '',
  );
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');

  // Stable UUID retained for the lifetime of a single submit attempt.
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  // Re-generate the key when the action type changes (new attempt).
  useEffect(() => {
    idempotencyKeyRef.current = crypto.randomUUID();
  }, [actionType]);

  if (isTerminal) {
    return (
      <p className="text-sm text-gray-500 italic">
        {t('pages:supportCaseClosed')}
      </p>
    );
  }

  if (availableActions.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        {t('pages:noEligibleActions')}
      </p>
    );
  }

  const selectedNeedsVersion =
    actionType !== '' && VERSION_REQUIRED_ACTIONS.has(actionType as SupportActionType);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!actionType) return;

    if (note.length > 500) {
      setNoteError(t('forms:noteTooLong'));
      return;
    }
    setNoteError('');

    const params: Parameters<Props['onSubmit']>[0] = {
      actionType: actionType as SupportActionType,
      idempotencyKey: idempotencyKeyRef.current,
      note: note.trim() || undefined,
    };

    if (selectedNeedsVersion && booking?.version !== undefined) {
      params.expectedBookingVersion = booking.version;
    }

    onSubmit(params);

    // Generate a fresh key for the next submit attempt.
    idempotencyKeyRef.current = crypto.randomUUID();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor={`${idPrefix}-action-type`}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {t('pages:actionType')}
        </label>
        <select
          id={`${idPrefix}-action-type`}
          value={actionType}
          onChange={e => setActionType(e.target.value as SupportActionType)}
          disabled={isSubmitting}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {availableActions.map(a => (
            <option key={a} value={a}>
              {t(ACTION_TYPE_I18N[a])}
            </option>
          ))}
        </select>
      </div>

      {selectedNeedsVersion && booking?.version === undefined && (
        <p className="text-xs text-amber-600">
          {t('pages:bookingVersionUnavailable')}
        </p>
      )}

      <div>
        <label
          htmlFor={`${idPrefix}-note`}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {t('forms:noteOptional')}
        </label>
        <textarea
          id={`${idPrefix}-note`}
          value={note}
          onChange={e => setNote(e.target.value)}
          disabled={isSubmitting}
          maxLength={500}
          rows={3}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        />
        {noteError && (
          <p className="text-xs text-red-600 mt-1">{noteError}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">{note.length}/500</p>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting || !actionType}
          className="flex-1 bg-primary-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          {isSubmitting ? t('pages:submitting') : t('pages:submitAction')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {t('common:cancel')}
        </button>
      </div>
    </form>
  );
};
